import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Input,
  Spinner,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import useUser from '../../hooks/useUser';
import { errorMessage, estimateApi } from '../estimate/estimateApi';

/**
 * The company's other pricing options (work the estimator doesn't calculate,
 * like a metal roof). Salespeople pick from these on an estimate and enter
 * the price there. Estimates keep their own copy, so editing or deleting an
 * option here never changes estimates already prepared.
 */
const SavedPricingOptions = () => {
  const { user } = useUser();
  const toast = useToast();
  const api = useMemo(() => (user ? estimateApi(user) : null), [user]);
  const [options, setOptions] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!api) return;
    api
      .get('/api/prices/saved-options')
      .then(setOptions)
      .catch(error => console.error('Could not load options', error))
      .finally(() => setLoading(false));
  }, [api]);

  const fail = (title, error) =>
    toast({
      title,
      description: errorMessage(error),
      status: 'error',
      duration: 5000,
      isClosable: true,
    });

  // Unsaved edits per option; "new" is the option being added.
  const draftFor = option =>
    drafts[option.id] ?? { name: option.name, description: option.description };
  // A first edit starts from the option's saved name and description, so
  // changing only one field keeps the other.
  const setDraft = (id, changes) =>
    setDrafts(current => {
      const option = options.find(o => o.id === id);
      const base =
        current[id] ??
        (option
          ? { name: option.name, description: option.description }
          : { name: '', description: '' });
      return { ...current, [id]: { ...base, ...changes } };
    });
  const clearDraft = id =>
    setDrafts(({ [id]: _removed, ...rest }) => rest);

  const sortByName = list =>
    [...list].sort((a, b) => a.name.localeCompare(b.name));

  const save = async (id, draft) => {
    setBusyId(id);
    try {
      const result = await api.post('/api/prices/saved-options', {
        id: id === 'new' ? undefined : id,
        name: draft.name,
        description: draft.description,
      });
      setOptions(list =>
        sortByName([...list.filter(o => o.id !== result.id), result])
      );
      clearDraft(id);
    } catch (error) {
      fail('Could not save the option', error);
    }
    setBusyId(null);
  };

  const remove = async option => {
    if (
      !window.confirm(
        `Delete "${option.name}"? Estimates that already include it keep their copy.`
      )
    ) {
      return;
    }
    setBusyId(option.id);
    try {
      await api.post('/api/prices/saved-options/delete', { id: option.id });
      setOptions(list => list.filter(o => o.id !== option.id));
      clearDraft(option.id);
    } catch (error) {
      fail('Could not delete the option', error);
    }
    setBusyId(null);
  };

  const fields = (id, draft) => (
    <VStack align="stretch" spacing={2}>
      <Input
        size="sm"
        value={draft.name}
        onChange={e => setDraft(id, { name: e.target.value })}
        placeholder="Name, e.g. Standing Seam Metal Roof"
        maxLength={120}
      />
      <Textarea
        size="sm"
        value={draft.description}
        onChange={e => setDraft(id, { description: e.target.value })}
        placeholder="Scope of work"
        rows={5}
      />
    </VStack>
  );

  if (loading) return <Spinner />;

  const newDraft = drafts.new;

  return (
    <VStack align="stretch" spacing={4} maxW="700px" mx="auto" textAlign="left">
      {options.length === 0 && !newDraft && (
        <Text fontSize="sm" color="gray.500">
          No options yet.
        </Text>
      )}

      {options.map(option => {
        const draft = draftFor(option);
        const isChanged =
          draft.name !== option.name ||
          (draft.description || '') !== (option.description || '');
        return (
          <Box
            key={option.id}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
            p={3}
          >
            {fields(option.id, draft)}
            <HStack justify="flex-end" spacing={2} mt={2}>
              <Button
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => remove(option)}
                isDisabled={busyId === option.id}
              >
                Delete
              </Button>
              {isChanged && (
                <>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => clearDraft(option.id)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="xs"
                    colorScheme="blue"
                    onClick={() => save(option.id, draft)}
                    isLoading={busyId === option.id}
                    isDisabled={!draft.name?.trim()}
                  >
                    Save
                  </Button>
                </>
              )}
            </HStack>
          </Box>
        );
      })}

      {newDraft ? (
        <Box borderWidth="1px" borderColor="blue.200" borderRadius="md" p={3}>
          {fields('new', newDraft)}
          <HStack justify="flex-end" spacing={2} mt={2}>
            <Button size="xs" variant="ghost" onClick={() => clearDraft('new')}>
              Cancel
            </Button>
            <Button
              size="xs"
              colorScheme="blue"
              onClick={() => save('new', newDraft)}
              isLoading={busyId === 'new'}
              isDisabled={!newDraft.name?.trim()}
            >
              Add
            </Button>
          </HStack>
        </Box>
      ) : (
        <Box>
          <Button
            size="sm"
            leftIcon={<AddIcon boxSize={2.5} />}
            onClick={() => setDraft('new', { name: '', description: '' })}
          >
            Add Option
          </Button>
        </Box>
      )}
    </VStack>
  );
};

export default SavedPricingOptions;
